import { useEffect } from 'react';

import { useErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { observer } from 'mobx-react-lite';

import { useStores } from '@shared/hooks';
import {
  BoundaryErrorType,
  FlkCode,
  addTestAttribute,
  getMismatches,
  useTimer,
} from '@shared/index';

import { useRefreshCode } from '../../api';
import { ResendButton, ResendComponent } from './resend.styles';
import type { ResendProps } from './resend.types';

const EXPIRE_PERIOD = 30;
const getExpiryTimestamp = () => Date.now() / 1000 + EXPIRE_PERIOD;

export const Resend = observer(
  ({
    onSuccess,
    onError,
    onResend,
    isRequestLimitExceeded,
    phone,
    otpType,
  }: ResendProps) => {
    const { t } = useTranslation();
    const {
      MainStore: {
        otpCooldownStore: { getExpiresAt },
      },
    } = useStores();
    const { showBoundary } = useErrorBoundary();
    // Resend new code timer

    const cooldownExpiryTimestamp = getExpiresAt(otpType);

    const { seconds, restart } = useTimer({
      expiryTimestamp: 0,
      syncWithExpiry: false,
    });
    // Cooldown timer
    const { isRunning: isCooldown, timer: cooldownTimer } = useTimer({
      expiryTimestamp: cooldownExpiryTimestamp ?? 0,
      isPaused: !cooldownExpiryTimestamp,
      syncWithExpiry: true,
    });
    const { minutes: cooldownMinutes, seconds: cooldownSeconds } =
      cooldownTimer;

    const { res, refetch, error, isFetching } = useRefreshCode({
      phone,
      deps: [phone],
    });

    const handleResend = () => {
      refetch();
      onResend?.();
      restart(getExpiryTimestamp());
    };

    useEffect(() => {
      if (!isFetching && res) {
        onSuccess && onSuccess(res.token);
      }
    }, [isFetching, res]);

    useEffect(() => {
      if (error) {
        const mismatches = getMismatches(error);

        if (!!mismatches) {
          mismatches.forEach(({ code }) => {
            if (
              code === FlkCode.OTP_0101_0001 ||
              code === FlkCode.OTP_0101_0002 ||
              code === FlkCode.OTP_0101_0003 ||
              code === FlkCode.OTP_0101_0004
            ) {
              // Неверный номер телефона
              onError?.({
                isLimitExceededError: code === FlkCode.OTP_0101_0004,
              });
            } else {
              showBoundary(BoundaryErrorType.SomethingWentWrong);
            }
          });
        } else {
          showBoundary(BoundaryErrorType.SomethingWentWrong);
        }
      }
    }, [error]);

    const cooldownReason = isRequestLimitExceeded
      ? 'OTP:hints.requestLimitExpirationIn'
      : 'OTP:hints.cooldownExpirationIn';
    const minutesText = String(cooldownMinutes).padStart(2, '0');
    const secondsText = String(cooldownSeconds).padStart(2, '0');
    const resendSecondsText = String(seconds).padStart(2, '0');

    return (
      <ResendComponent>
        {isCooldown ? (
          <pre>
            {t(cooldownReason, {
              minutes: minutesText,
              seconds: secondsText,
            })}
          </pre>
        ) : seconds > 0 ? (
          t('COMMON:hints.getNewCodeIn', {
            seconds: resendSecondsText,
          })
        ) : (
          <ResendButton
            onClick={handleResend}
            {...addTestAttribute('otp.resendButton')}
          >
            {t('COMMON:buttons.getNewCode')}
          </ResendButton>
        )}
      </ResendComponent>
    );
  }
);
